import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, Loader2, Unplug, Link2, Sparkles } from 'lucide-react';
import { GoogleKeysForm } from './GoogleKeysForm';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const GoogleAccountCard = () => {
  const [st, setSt] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/google/status`, { withCredentials: true });
      setSt(r.data);
      if (r.data.connected) {
        axios.get(`${API}/google/agenda`, { withCredentials: true }).then(a => setAgenda(a.data)).catch(() => {});
      } else if (r.data.configured) {
        axios.get(`${API}/google/precheck`, { withCredentials: true }).then(c => setCheck(c.data)).catch(() => {});
      }
    } catch { setSt({ configured: false, connected: false }); }
  };

  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    if (q.get('google') === 'connected') toast.success('Google account connected — TI can now add to your Calendar & Tasks');
    if (q.get('google') === 'error') {
      const reason = q.get('reason') || 'unknown';
      toast.error(reason === 'access_denied'
        ? 'Google refused the sign-in (403 access_denied): add your email under OAuth consent screen → Test users, or publish the app, then retry'
        : `Google connection failed: ${reason}`, { duration: 12000 });
    }
    if (q.get('google')) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/google/connect`, { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Google sign-in'); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Google account? Existing events/tasks stay in Google.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/google/disconnect`, { withCredentials: true }); toast('Google disconnected'); setAgenda(null); await load(); }
    catch { toast.error('Disconnect failed'); }
    finally { setBusy(false); }
  };

  const togglePref = async (k) => {
    const next = { ...st.prefs, [k]: !st.prefs[k] };
    setSt(s => ({ ...s, prefs: next }));
    try { await axios.patch(`${API}/google/prefs`, { [k]: next[k] }, { withCredentials: true }); }
    catch { toast.error('Could not save preference'); load(); }
  };

  if (!st) return null;
  const Toggle = ({ k, label }) => (
    <button type="button" onClick={() => togglePref(k)} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${st.prefs?.[k] ? 'bg-emerald-500/15 border-emerald-700 text-emerald-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`} data-testid={`google-pref-${k}`}>
      <span className={`w-2 h-2 rounded-full ${st.prefs?.[k] ? 'bg-emerald-400' : 'bg-zinc-600'}`} /> {label}
    </button>
  );

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid="google-account-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${st.connected ? 'bg-emerald-500/15 text-emerald-400' : 'bg-sky-500/15 text-sky-400'}`}>
            <CalendarCheck size={18} />
          </div>
          <div>
            <div className="text-zinc-50 text-sm font-semibold">
              Google Calendar &amp; Tasks —
              {st.connected
                ? <span className="text-emerald-400 ml-1" data-testid="google-connected-email">{st.email}</span>
                : <span className="text-amber-300 ml-1">Not connected</span>}
            </div>
            <div className="text-zinc-500 text-xs mt-1 max-w-xl">
              Tell TI <span className="text-amber-300">"remind me tomorrow 10 am to call Anil"</span> or <span className="text-amber-300">"add task: send Patel samples by Friday"</span> — in Hey TI or on WhatsApp — and it lands in <b>your</b> Google Calendar (with a popup alert) and Google Tasks, plus the ERP alarm. Gemini 3 Flash does the parsing.
              {!st.connected && <span className="block mt-1">Google asks each person to approve once — that's the "Connect" below. Nothing else to set up.</span>}
              {st.error && <span className="block mt-1 text-rose-300">Google session problem ({st.error}) — reconnect.</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {st.connected ? (
            <button onClick={disconnect} disabled={busy} className="px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-xs flex items-center gap-1.5 hover:border-rose-700 hover:text-rose-300" data-testid="google-disconnect-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Unplug size={12} />} Disconnect
            </button>
          ) : (
            <button onClick={connect} disabled={busy || !st.configured} className="px-3 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-black text-xs font-semibold flex items-center gap-1.5 disabled:bg-zinc-700 disabled:text-zinc-400" data-testid="google-connect-btn" title={st.configured ? '' : 'Google keys not set yet'}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Connect Google
            </button>
          )}
        </div>
      </div>

      {!st.connected && st.configured && check && !check.ok && (
        <div className="mt-4 rounded-xl border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200" data-testid="google-precheck-warning">
          <div className="font-semibold">Google will reject the sign-in: <span className="font-mono">{check.error}</span></div>
          <div className="text-rose-200/80 mt-1">{check.hint}</div>
          {check.redirect_uri && (
            <button type="button" onClick={() => { navigator.clipboard?.writeText(check.redirect_uri); toast('Redirect URI copied'); }} className="mt-2 w-full flex items-center justify-between gap-2 font-mono text-[11px] text-zinc-200 bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-1.5 hover:border-zinc-600" data-testid="google-precheck-uri">
              <span className="truncate">{check.redirect_uri}</span><span className="text-zinc-500 shrink-0">copy</span>
            </button>
          )}
          <button type="button" onClick={() => { setCheck(null); load(); }} className="mt-2 text-[11px] underline text-rose-300 hover:text-rose-100" data-testid="google-precheck-retry">Re-check</button>
        </div>
      )}
      {!st.connected && st.configured && check?.ok && (
        <div className="mt-3 text-[11px] text-emerald-300/90" data-testid="google-precheck-ok">Google accepts this app's sign-in URL — click Connect Google. If Google shows "access blocked / not verified", add your email as a Test user on the OAuth consent screen.</div>
      )}

      {st.connected && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-zinc-500 mr-1">TI reminders also go to:</span>
          <Toggle k="calendar" label="Google Calendar" />
          <Toggle k="tasks" label="Google Tasks" />
          {agenda && (
            <span className="ml-auto text-[11px] text-zinc-400 flex items-center gap-1" data-testid="google-agenda-summary">
              <Sparkles size={11} className="text-amber-400" /> Today: {agenda.events?.length || 0} event{agenda.events?.length === 1 ? '' : 's'} · {agenda.tasks?.length || 0} task{agenda.tasks?.length === 1 ? '' : 's'}{agenda.overdue_tasks?.length ? ` · ${agenda.overdue_tasks.length} overdue` : ''} · {agenda.erp_reminders?.length || 0} ERP reminder{agenda.erp_reminders?.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {!st.configured && !st.can_manage_keys && (
        <div className="mt-3 text-[11px] text-amber-300/90" data-testid="google-not-configured">Google isn't set up for the company yet — ask the admin to add the Google OAuth keys.</div>
      )}
      {st.can_manage_keys && (
        <div className="mt-3">
          {(!st.configured || showKeys)
            ? <GoogleKeysForm redirectUri={st.redirect_uri} onSaved={load} />
            : <button type="button" onClick={() => setShowKeys(true)} className="text-[11px] text-zinc-500 hover:text-zinc-300 underline" data-testid="google-keys-toggle">Google OAuth keys (admin)</button>}
        </div>
      )}
    </div>
  );
};
